import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Plus, Edit, Trash2, Search, Loader2, CheckCircle, XCircle,
  Trophy, Target, Shield, Brain, Scale, Leaf, Award, Zap,
  Play, Pause, Users, BarChart3, Calendar, Clock, BookOpen,
  Beaker, ChevronRight, X, Info, Sparkles, AlertCircle, Eye
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import ContentPreviewModal, { StatusBadge } from './ContentPreviewModal';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

// Campaign type configurations
const CAMPAIGN_TYPES = {
  compliance: { 
    name: 'Compliance', 
    icon: Shield, 
    color: 'bg-blue-500',
    badge: 'Compliance Champion',
    description: 'Regulatory compliance training campaigns'
  },
  cyber_awareness: { 
    name: 'Cyber Awareness', 
    icon: Target, 
    color: 'bg-purple-500',
    badge: 'Cyber Guardian',
    description: 'Cybersecurity and data protection training'
  },
  ai_governance: { 
    name: 'AI Governance', 
    icon: Brain, 
    color: 'bg-cyan-500',
    badge: 'AI Pioneer',
    description: 'AI ethics and governance training'
  },
  ethics: { 
    name: 'Ethics', 
    icon: Scale, 
    color: 'bg-green-500',
    badge: 'Ethics Leader',
    description: 'Business ethics and conduct training'
  },
  risk_management: { 
    name: 'Risk Management', 
    icon: Target, 
    color: 'bg-amber-500',
    badge: 'Risk Navigator',
    description: 'Risk assessment and mitigation training'
  },
  esg: { 
    name: 'ESG', 
    icon: Leaf, 
    color: 'bg-emerald-500',
    badge: 'ESG Advocate',
    description: 'Environmental, Social, Governance training'
  },
  mandatory_training: { 
    name: 'Mandatory Training', 
    icon: CheckCircle, 
    color: 'bg-red-500',
    badge: 'Training Complete',
    description: 'Required compliance training for all staff'
  },
  custom: { 
    name: 'Custom', 
    icon: Award, 
    color: 'bg-navigate-gold',
    badge: 'Campaign Complete',
    description: 'Custom campaign with configurable badge'
  },
};

export default function CampaignsAdmin({ token }) {
  const [campaigns, setCampaigns] = useState([]);
  const [courses, setCourses] = useState([]);
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showActivitiesDialog, setShowActivitiesDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    campaign_type: 'custom',
    bonus_xp: 100,
  });
  const [saving, setSaving] = useState(false);

  // Fetch data
  useEffect(() => {
    fetchCampaigns();
    fetchCourses();
    fetchLabs();
  }, [token]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaigns(res.data.campaigns || res.data || []);
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const res = await axios.get(`${API}/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(res.data || []);
    } catch (err) {
      console.error('Failed to fetch courses:', err);
    }
  };

  const fetchLabs = async () => {
    try {
      const res = await axios.get(`${API}/learning-labs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLabs(res.data.labs || res.data || []);
    } catch (err) {
      console.error('Failed to fetch labs:', err);
    }
  };

  // Create campaign
  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Campaign name is required');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/admin/campaigns`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Campaign created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchCampaigns();
    } catch (err) {
      console.error('Failed to create campaign:', err);
      toast.error(err.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setSaving(false);
    }
  };

  // Update campaign
  const handleUpdate = async () => {
    if (!selectedCampaign) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/admin/campaigns/${selectedCampaign.campaign_id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Campaign updated successfully');
      setShowEditDialog(false);
      setSelectedCampaign(null);
      resetForm();
      fetchCampaigns();
    } catch (err) {
      console.error('Failed to update campaign:', err);
      toast.error(err.response?.data?.detail || 'Failed to update campaign');
    } finally {
      setSaving(false);
    }
  };

  // Delete campaign
  const handleDelete = async () => {
    if (!selectedCampaign) return;
    
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/campaigns/${selectedCampaign.campaign_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Campaign deleted successfully');
      setShowDeleteDialog(false);
      setSelectedCampaign(null);
      fetchCampaigns();
    } catch (err) {
      console.error('Failed to delete campaign:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete campaign');
    } finally {
      setSaving(false);
    }
  };

  // Toggle campaign active status
  const toggleCampaignStatus = async (campaign) => {
    const endpoint = campaign.is_active ? 'deactivate' : 'activate';
    try {
      await axios.post(`${API}/admin/campaigns/${campaign.campaign_id}/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Campaign ${campaign.is_active ? 'deactivated' : 'activated'}`);
      fetchCampaigns();
    } catch (err) {
      console.error('Failed to toggle campaign status:', err);
      toast.error('Failed to update campaign status');
    }
  };

  // Add activity to campaign
  const addActivity = async (activityType, referenceId, title) => {
    if (!selectedCampaign) return;
    
    try {
      await axios.post(
        `${API}/admin/campaigns/${selectedCampaign.campaign_id}/activities`,
        {
          activity_type: activityType,
          reference_id: referenceId,
          title: title,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Activity added to campaign');
      // Refresh campaign data
      const res = await axios.get(`${API}/admin/campaigns/${selectedCampaign.campaign_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCampaign(res.data);
      fetchCampaigns();
    } catch (err) {
      console.error('Failed to add activity:', err);
      toast.error(err.response?.data?.detail || 'Failed to add activity');
    }
  };

  // Remove activity from campaign
  const removeActivity = async (activityId) => {
    if (!selectedCampaign) return;
    
    try {
      await axios.delete(
        `${API}/admin/campaigns/${selectedCampaign.campaign_id}/activities/${activityId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Activity removed from campaign');
      // Refresh campaign data
      const res = await axios.get(`${API}/admin/campaigns/${selectedCampaign.campaign_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCampaign(res.data);
      fetchCampaigns();
    } catch (err) {
      console.error('Failed to remove activity:', err);
      toast.error('Failed to remove activity');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      campaign_type: 'custom',
      bonus_xp: 100,
    });
  };

  const openEditDialog = (campaign) => {
    setSelectedCampaign(campaign);
    setFormData({
      name: campaign.name,
      description: campaign.description || '',
      campaign_type: campaign.campaign_type || 'custom',
      bonus_xp: campaign.bonus_xp || 100,
    });
    setShowEditDialog(true);
  };

  const openActivitiesDialog = async (campaign) => {
    try {
      const res = await axios.get(`${API}/admin/campaigns/${campaign.campaign_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCampaign(res.data);
      setShowActivitiesDialog(true);
    } catch (err) {
      console.error('Failed to fetch campaign details:', err);
      toast.error('Failed to load campaign details');
    }
  };

  // Filter campaigns
  const filteredCampaigns = campaigns.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get activities not yet in campaign
  const getAvailableCourses = () => {
    if (!selectedCampaign?.activities) return courses;
    const usedIds = selectedCampaign.activities.map(a => a.reference_id);
    return courses.filter(c => !usedIds.includes(c.course_id));
  };

  const getAvailableLabs = () => {
    if (!selectedCampaign?.activities) return labs;
    const usedIds = selectedCampaign.activities.map(a => a.reference_id);
    return labs.filter(l => !usedIds.includes(l.lab_id));
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header with explanation */}
      <div className="bg-gradient-to-r from-navigate-gold/10 to-amber-500/10 border border-navigate-gold/30 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-navigate-gold/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Trophy className="h-6 w-6 text-navigate-gold" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white mb-2">Campaign Management</h1>
            <p className="text-zinc-400 mb-4">
              Create time-bound learning campaigns to drive engagement. Campaigns bundle multiple courses and Learning Labs 
              into focused initiatives. Learners who complete ALL activities in a campaign earn a <span className="text-navigate-gold font-medium">special badge</span> and <span className="text-navigate-gold font-medium">bonus XP</span>.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2 text-zinc-300">
                <Sparkles className="h-4 w-4 text-purple-400" />
                <span>Bundle courses into focused sprints</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-300">
                <Award className="h-4 w-4 text-amber-400" />
                <span>Award exclusive campaign badges</span>
              </div>
              <div className="flex items-center gap-2 text-zinc-300">
                <Zap className="h-4 w-4 text-yellow-400" />
                <span>Grant bonus XP on completion</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-700"
          />
        </div>
        <Button 
          onClick={() => { resetForm(); setShowCreateDialog(true); }}
          className="bg-navigate-gold hover:bg-navigate-gold/90 text-black font-medium"
          data-testid="create-campaign-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Campaign
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
          <div className="text-2xl font-bold text-white">{campaigns.length}</div>
          <div className="text-sm text-zinc-500">Total Campaigns</div>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-400">
            {campaigns.filter(c => c.is_active).length}
          </div>
          <div className="text-sm text-zinc-500">Active</div>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
          <div className="text-2xl font-bold text-zinc-400">
            {campaigns.filter(c => !c.is_active).length}
          </div>
          <div className="text-sm text-zinc-500">Inactive</div>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
          <div className="text-2xl font-bold text-navigate-gold">
            {campaigns.reduce((sum, c) => sum + (c.activities?.length || 0), 0)}
          </div>
          <div className="text-sm text-zinc-500">Total Activities</div>
        </div>
      </div>

      {/* Campaigns List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
          <Trophy className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No campaigns yet</h3>
          <p className="text-zinc-500 mb-6">Create your first campaign to start driving learner engagement</p>
          <Button 
            onClick={() => { resetForm(); setShowCreateDialog(true); }}
            className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Campaign
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredCampaigns.map((campaign) => {
            const typeConfig = CAMPAIGN_TYPES[campaign.campaign_type] || CAMPAIGN_TYPES.custom;
            const Icon = typeConfig.icon;
            
            return (
              <div 
                key={campaign.campaign_id}
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-colors"
                data-testid={`campaign-row-${campaign.campaign_id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`w-12 h-12 ${typeConfig.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-semibold text-white truncate">{campaign.name}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        campaign.is_active 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-zinc-700 text-zinc-400'
                      }`}>
                        {campaign.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-500 line-clamp-1 mb-3">{campaign.description}</p>
                    
                    {/* Meta info */}
                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      <div className="flex items-center gap-1.5 text-zinc-400">
                        <BookOpen className="h-4 w-4" />
                        <span>{campaign.activities?.length || 0} activities</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-navigate-gold">
                        <Zap className="h-4 w-4" />
                        <span>+{campaign.bonus_xp} XP</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-amber-400">
                        <Award className="h-4 w-4" />
                        <span>{typeConfig.badge}</span>
                      </div>
                      {campaign.participants_count > 0 && (
                        <div className="flex items-center gap-1.5 text-zinc-400">
                          <Users className="h-4 w-4" />
                          <span>{campaign.participants_count} enrolled</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        try {
                          const res = await axios.get(`${API}/admin/campaigns/${campaign.campaign_id}`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          setSelectedCampaign(res.data);
                          setShowPreviewModal(true);
                        } catch (err) {
                          toast.error('Failed to load campaign');
                        }
                      }}
                      className="text-blue-400 hover:text-blue-300"
                      title="Preview Campaign"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openActivitiesDialog(campaign)}
                      className="text-zinc-400 hover:text-white"
                      title="Manage Activities"
                    >
                      <BookOpen className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(campaign)}
                      className="text-zinc-400 hover:text-white"
                      title="Edit Campaign"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleCampaignStatus(campaign)}
                      className={campaign.is_active ? 'text-green-400 hover:text-green-300' : 'text-zinc-500 hover:text-white'}
                      title={campaign.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {campaign.is_active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSelectedCampaign(campaign); setShowDeleteDialog(true); }}
                      className="text-zinc-400 hover:text-red-400"
                      title="Delete Campaign"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Campaign Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trophy className="h-5 w-5 text-navigate-gold" />
              Create New Campaign
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Define your campaign details. You can add courses and Learning Labs after creation.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Campaign Name *</Label>
              <Input
                placeholder="e.g., Q3 Compliance Sprint"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Description</Label>
              <Textarea
                placeholder="Describe the campaign objectives and what learners will achieve..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-zinc-800 border-zinc-700 min-h-[100px]"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Campaign Type</Label>
              <Select 
                value={formData.campaign_type} 
                onValueChange={(v) => setFormData({ ...formData, campaign_type: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {Object.entries(CAMPAIGN_TYPES).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <config.icon className="h-4 w-4" />
                        <span>{config.name}</span>
                        <span className="text-zinc-500 text-xs">({config.badge})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500">
                {CAMPAIGN_TYPES[formData.campaign_type]?.description}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Bonus XP Reward</Label>
              <Input
                type="number"
                min="0"
                step="50"
                value={formData.bonus_xp}
                onChange={(e) => setFormData({ ...formData, bonus_xp: parseInt(e.target.value) || 0 })}
                className="bg-zinc-800 border-zinc-700 w-32"
              />
              <p className="text-xs text-zinc-500">
                XP awarded when learner completes ALL campaign activities
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreate}
              disabled={saving || !formData.name.trim()}
              className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Campaign Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="h-5 w-5 text-navigate-gold" />
              Edit Campaign
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Campaign Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-zinc-800 border-zinc-700 min-h-[100px]"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Campaign Type</Label>
              <Select 
                value={formData.campaign_type} 
                onValueChange={(v) => setFormData({ ...formData, campaign_type: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {Object.entries(CAMPAIGN_TYPES).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <config.icon className="h-4 w-4" />
                        <span>{config.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Bonus XP Reward</Label>
              <Input
                type="number"
                min="0"
                step="50"
                value={formData.bonus_xp}
                onChange={(e) => setFormData({ ...formData, bonus_xp: parseInt(e.target.value) || 0 })}
                className="bg-zinc-800 border-zinc-700 w-32"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdate}
              disabled={saving || !formData.name.trim()}
              className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Activities Dialog */}
      <Dialog open={showActivitiesDialog} onOpenChange={setShowActivitiesDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-navigate-gold" />
              Manage Campaign Activities
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Add courses and Learning Labs to &ldquo;{selectedCampaign?.name}&rdquo;. Learners must complete ALL activities to earn the badge.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-6 py-4">
            {/* Current Activities */}
            <div>
              <h4 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-400" />
                Current Activities ({selectedCampaign?.activities?.length || 0})
              </h4>
              {selectedCampaign?.activities?.length > 0 ? (
                <div className="space-y-2">
                  {selectedCampaign.activities.map((activity, index) => (
                    <div 
                      key={activity.activity_id}
                      className="flex items-center justify-between bg-zinc-800/50 rounded-lg p-3"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 bg-zinc-700 rounded-full flex items-center justify-center text-xs text-zinc-400">
                          {index + 1}
                        </span>
                        {activity.activity_type === 'course' ? (
                          <BookOpen className="h-4 w-4 text-blue-400" />
                        ) : (
                          <Beaker className="h-4 w-4 text-purple-400" />
                        )}
                        <div>
                          <p className="text-sm text-white">{activity.title}</p>
                          <p className="text-xs text-zinc-500 capitalize">{activity.activity_type}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeActivity(activity.activity_id)}
                        className="text-zinc-400 hover:text-red-400"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-zinc-800/30 rounded-lg p-4 text-center">
                  <AlertCircle className="h-8 w-8 text-zinc-600 mx-auto mb-2" />
                  <p className="text-sm text-zinc-500">No activities added yet. Add courses or labs below.</p>
                </div>
              )}
            </div>

            {/* Add Courses */}
            <div>
              <h4 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-blue-400" />
                Add Courses
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {getAvailableCourses().length > 0 ? (
                  getAvailableCourses().slice(0, 10).map((course) => (
                    <div 
                      key={course.course_id}
                      className="flex items-center justify-between bg-zinc-800/30 hover:bg-zinc-800/50 rounded-lg p-3 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{course.title}</p>
                        <p className="text-xs text-zinc-500">{course.category || 'Uncategorized'}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => addActivity('course', course.course_id, course.title)}
                        className="text-navigate-gold hover:text-navigate-gold/80"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-zinc-500 text-center py-4">All courses already added</p>
                )}
              </div>
            </div>

            {/* Add Learning Labs */}
            <div>
              <h4 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
                <Beaker className="h-4 w-4 text-purple-400" />
                Add Learning Labs
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {getAvailableLabs().length > 0 ? (
                  getAvailableLabs().slice(0, 10).map((lab) => (
                    <div 
                      key={lab.lab_id}
                      className="flex items-center justify-between bg-zinc-800/30 hover:bg-zinc-800/50 rounded-lg p-3 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{lab.title}</p>
                        <p className="text-xs text-zinc-500">{lab.industry || 'General'}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => addActivity('learning_lab', lab.lab_id, lab.title)}
                        className="text-navigate-gold hover:text-navigate-gold/80"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-zinc-500 text-center py-4">All Learning Labs already added</p>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter className="border-t border-zinc-800 pt-4">
            <Button variant="ghost" onClick={() => setShowActivitiesDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-400" />
              Delete Campaign
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Are you sure you want to delete &ldquo;{selectedCampaign?.name}&rdquo;? This action cannot be undone.
              Learners who already earned the badge will keep it.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDelete}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Preview Modal */}
      <ContentPreviewModal
        open={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setSelectedCampaign(null);
        }}
        contentType="campaign"
        content={selectedCampaign || {}}
        token={token}
        onPublish={async () => {
          if (selectedCampaign && !selectedCampaign.is_active) {
            await toggleCampaignStatus(selectedCampaign);
          }
        }}
        onSaveDraft={async () => {
          // Campaign is already saved, just close
        }}
      />
    </div>
  );
}
